import React from "react";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import ApartmentIcon from "@material-ui/icons/Apartment";
import FavoriteBorderIcon from "@material-ui/icons/FavoriteBorder";
import GavelIcon from "@material-ui/icons/Gavel";

import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import { APP_STRINGS } from "constants/appStrings";

const useStyles = makeStyles((theme) => ({
  section: {
    width: "min(1240px, calc(100% - 48px))",
    margin: "0 auto",
    padding: theme.spacing(6, 0, 2),
    [theme.breakpoints.down("sm")]: {
      width: "min(100% - 32px, 1240px)",
    },
  },
  header: {
    maxWidth: 760,
    marginBottom: theme.spacing(5),
  },
  eyebrow: {
    display: "inline-flex",
    padding: theme.spacing(1, 2),
    borderRadius: 999,
    background: "rgba(20, 25, 34, 0.06)",
    color: "#8d6d42",
    fontSize: 12,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.14em",
  },
  title: {
    margin: theme.spacing(2, 0),
    fontFamily: '"Cormorant Garamond", "Times New Roman", serif',
    fontSize: "clamp(2.6rem, 4.2vw, 4rem)",
    lineHeight: 0.96,
    color: "#5d3342",
  },
  subtitle: {
    fontSize: 18,
    lineHeight: 1.9,
    color: "#5f6775",
  },
  areaCard: {
    height: "100%",
    padding: theme.spacing(3.5),
    borderRadius: 30,
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(249,245,239,0.92) 100%)",
    border: "1px solid rgba(31, 38, 50, 0.08)",
    boxShadow: "0 26px 60px rgba(22, 29, 40, 0.08)",
  },
  iconWrap: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 58,
    height: 58,
    borderRadius: 18,
    background: "rgba(212, 179, 127, 0.16)",
    color: "#8c6a3e",
    marginBottom: theme.spacing(2),
  },
  cardTitle: {
    fontFamily: '"Cormorant Garamond", "Times New Roman", serif',
    fontSize: 34,
    color: "#5d3342",
    marginBottom: theme.spacing(1),
  },
  cardText: {
    color: "#5f6775",
    fontSize: 16,
    lineHeight: 1.85,
  },
  bulletList: {
    margin: theme.spacing(2.5, 0, 0),
    paddingLeft: theme.spacing(2.5),
    color: "#343b47",
    "& li": {
      marginBottom: theme.spacing(1.2),
      lineHeight: 1.8,
    },
  },
  strategyWrap: {
    marginTop: theme.spacing(5),
    padding: theme.spacing(4),
    borderRadius: 34,
    background: "linear-gradient(135deg, #6d3d4d 0%, #8b5163 100%)",
    color: "#f7f2ea",
    boxShadow: "0 34px 80px rgba(98, 54, 69, 0.16)",
  },
  strategyTitle: {
    fontFamily: '"Cormorant Garamond", "Times New Roman", serif',
    fontSize: "clamp(2.2rem, 3.4vw, 3.3rem)",
    color: "#fff7ed",
    marginBottom: theme.spacing(1.5),
  },
  strategyText: {
    color: "rgba(247, 242, 234, 0.8)",
    fontSize: 17,
    lineHeight: 1.9,
  },
  methodGrid: {
    marginTop: theme.spacing(3),
  },
  methodCard: {
    height: "100%",
    padding: theme.spacing(2.5),
    borderRadius: 24,
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.1)",
  },
  methodTitle: {
    color: "#fff2dc",
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  methodText: {
    color: "rgba(247, 242, 234, 0.74)",
    lineHeight: 1.75,
  },
  cta: {
    marginTop: theme.spacing(4),
    minHeight: 54,
    padding: theme.spacing(0, 3),
    borderRadius: 999,
    background: "linear-gradient(135deg, #d1b07a 0%, #c39b63 100%)",
    color: "#5b3240",
    fontWeight: 700,
  },
}));

const areaIcons = [<GavelIcon />, <FavoriteBorderIcon />, <ApartmentIcon />];

export default function SkillsSection({ rollPageOnClickButton }) {
  const classes = useStyles();

  return (
    <div className={classes.section}>
      <div className={classes.header}>
        <span className={classes.eyebrow}>{APP_STRINGS.skills.eyebrow}</span>
        <Typography component="h2" className={classes.title}>
          {APP_STRINGS.skills.title}
        </Typography>
        <Typography component="p" className={classes.subtitle}>
          {APP_STRINGS.skills.subtitle}
        </Typography>
      </div>

      <GridContainer spacing={4}>
        {APP_STRINGS.skills.areas.map((area, index) => (
          <GridItem key={area.title} xs={12} md={4}>
            <div className={classes.areaCard}>
              <div className={classes.iconWrap}>{areaIcons[index]}</div>
              <Typography component="h3" className={classes.cardTitle}>
                {area.title}
              </Typography>
              <Typography component="p" className={classes.cardText}>
                {area.text}
              </Typography>
              <ul className={classes.bulletList}>
                {area.bullets.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </GridItem>
        ))}
      </GridContainer>

      <Box className={classes.strategyWrap}>
        <Typography component="h3" className={classes.strategyTitle}>
          {APP_STRINGS.skills.strategyTitle}
        </Typography>
        <Typography component="p" className={classes.strategyText}>
          {APP_STRINGS.skills.strategyText}
        </Typography>

        <GridContainer spacing={3} className={classes.methodGrid}>
          {APP_STRINGS.skills.methods.map((item) => (
            <GridItem key={item.title} xs={12} md={4}>
              <div className={classes.methodCard}>
                <Typography component="h4" className={classes.methodTitle}>
                  {item.title}
                </Typography>
                <Typography component="p" className={classes.methodText}>
                  {item.text}
                </Typography>
              </div>
            </GridItem>
          ))}
        </GridContainer>

        <Button
          className={classes.cta}
          onClick={() => rollPageOnClickButton("contact")}
        >
          {APP_STRINGS.skills.cta}
        </Button>
      </Box>
    </div>
  );
}
